import React, {useMemo, useState, useRef, useEffect} from 'react';
import SupplierActionsMenu from './SupplierActionsMenu';
import ConfirmDialog from './ConfirmDialog';

const REFERENCE_BG = '#E7F6FB';
const MUTED_BG = '#F5F5F5';

// The sidebar is `position: fixed`, so it doesn't move when the page scrolls horizontally. A sticky
// `left: 0` would stick to the viewport's edge, sliding the sticky column underneath the sidebar
// instead of stopping next to it — so `left` is instead measured as the actual distance from the
// viewport edge to the table's own container (i.e. past the sidebar), and recalculated whenever that
// distance can change (sidebar collapsed/expanded, window resized).
const stickyLeftStyle = (left) => ({position: 'sticky', left, zIndex: 1});

function formatMoney(value, currencyCode = 'USD') {
    if (value === null || value === undefined) {
        return '—';
    }
    return new Intl.NumberFormat(undefined, {style: 'currency', currency: currencyCode}).format(value);
}

function formatNumber(value) {
    if (value === null || value === undefined) {
        return '—';
    }
    return new Intl.NumberFormat().format(value);
}

function formatPercentage(value) {
    if (value === null || value === undefined) {
        return '—';
    }
    return `${value}%`;
}

function formatDateRange(minDate, maxDate) {
    if (!minDate && !maxDate) {
        return null;
    }
    const format = (iso) => (iso ? new Date(iso).toLocaleDateString() : null);
    return [format(minDate), format(maxDate)].filter(Boolean).join(' - ');
}

function formatUnit(value, unit) {
    if (value === null || value === undefined) {
        return null;
    }
    return `${formatNumber(value)} ${unit}`;
}

// Turnover/employees/companyAge fall back to legacy free-text range strings (e.g. '5 - 20M',
// '50 - 100', '< 5') when a company has no exact kyb_* figure on file (BuildPricingComparison#build_profile)
// — those already read fine on their own, so only the exact numeric figure gets a unit suffix appended.
function formatUnitOrRaw(value, unit) {
    if (value === null || value === undefined) {
        return null;
    }
    return typeof value === 'number' ? `${formatNumber(value)} ${unit}` : value;
}

// Each location on its own line within the cell, rather than one comma-joined line — easier to
// scan when a supplier has entered several warehouse/logistics locations.
function formatMultilineList(values) {
    if (!values || values.length === 0) {
        return null;
    }
    return values.map((value, index) => <div key={index}>{value}</div>);
}

// Whether a supplier has entered any of the four optional per-SKU fulfillment fields for a given
// order line — shared by the notes-expando badge and the always-visible Fulfillment Details row.
function hasSkuDetails(details) {
    return !!details && (details.leadTimeWeeks != null || details.shelfLifeDays != null || details.storageClass || details.moq != null);
}

// The optional per-SKU fulfillment details a supplier may have entered (spec 006 US5), shown as an
// always-visible row directly below Rebate Conditions (spec 016) rather than nested in each SKU's
// notes expando — one entry per order line that has at least one field set, labeled with that
// line's SKU name so values from different SKUs stay individually attributable (FR-002), with a
// "—" placeholder for any of the four fields that specific line left blank (FR-003).
function FulfillmentDetailsList({lines, proposal, i18n}) {
    const linesWithDetails = lines.filter((line) => hasSkuDetails(proposal?.skuDetails?.[line.id]));
    if (linesWithDetails.length === 0) {
        return '—';
    }
    return linesWithDetails.map((line) => {
        const details = proposal.skuDetails[line.id];
        const parts = [
            `${i18n.labelMoq}: ${details.moq ?? '—'}`,
            `${i18n.labelLeadTimeWeeks}: ${details.leadTimeWeeks != null ? formatUnit(details.leadTimeWeeks, 'wk') : '—'}`,
            `${i18n.labelShelfLifeDays}: ${details.shelfLifeDays != null ? formatUnit(details.shelfLifeDays, 'd') : '—'}`,
            `${i18n.labelStorageClass}: ${details.storageClass || '—'}`
        ];
        return (
            <div key={line.id} className="font-size-xxs text-muted mb-1">
                <div className="font-weight-bold">{line.lotName}</div>
                {parts.join(' · ')}
            </div>
        );
    });
}

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
}

// Opens (or creates, if this is the first contact — ConversationService::FindOrCreateDealConversation
// is idempotent) the deal conversation between the buyer's team and a supplier's team, then navigates
// there — reuses the existing /conversations endpoint as-is, no backend change needed
// (specs/010-pricing-comparison-enhancements US4, research.md Decision 6).
function messageSupplier(comparison, supplierTeamId) {
    fetch('/conversations', {
        method: 'POST',
        redirect: 'follow',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-CSRF-Token': csrfToken()
        },
        body: new URLSearchParams({
            conversation_type: 'deal',
            deal_id: comparison.dealId,
            buyer_team_id: comparison.buyerTeamId,
            supplier_team_id: supplierTeamId
        })
    }).then((response) => {
        window.location = response.url;
    });
}

// Tracks which suppliers have just been rejected from this page (so they can be removed from view
// without a reload, spec SC-003) and the confirmation dialog's open/pending state. Suppliers already
// rejected via any other entry point never reach the client at all — they're excluded server-side
// (BuildPricingComparison#deal_pricing_requests_scope) — this only covers a rejection made from this
// page in the current session (specs/010-pricing-comparison-enhancements US3).
function useSupplierRejection(comparison) {
    const [rejectedTeamIds, setRejectedTeamIds] = useState(() => new Set());
    const [pendingRejectSupplier, setPendingRejectSupplier] = useState(null);

    const filterRejected = (suppliers) => suppliers.filter((s) => !rejectedTeamIds.has(s.teamId));

    const confirmReject = () => {
        const supplier = pendingRejectSupplier;
        if (!supplier) {
            return;
        }

        fetch(`/deals/${comparison.dealUuid}/reject_request_from_interested`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'X-CSRF-Token': csrfToken(),
                Accept: 'application/json'
            },
            body: new URLSearchParams({supplier_team_uuid: supplier.teamUuid})
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.success) {
                    setRejectedTeamIds((prev) => new Set(prev).add(supplier.teamId));
                }
            })
            .finally(() => setPendingRejectSupplier(null));
    };

    return {
        rejectedTeamIds,
        filterRejected,
        pendingRejectSupplier,
        requestReject: setPendingRejectSupplier,
        cancelReject: () => setPendingRejectSupplier(null),
        confirmReject
    };
}

// Renders a plain Yes/No answer as a tick/cross, with no reasoning or counter-suggestion attached
// (unlike TermsAcceptanceValue/LaunchTimeframeValue) — used for Can Meet Packaging Requirements and
// Can Meet Logistics Requirements.
function BooleanCheckIndicator({canMeet}) {
    if (canMeet === null || canMeet === undefined) {
        return <span className="text-muted">—</span>;
    }
    return canMeet
        ? <i className="fas fa-check-circle text-success" style={{fontSize: '1.1rem'}}/>
        : <i className="fas fa-times-circle text-danger" style={{fontSize: '1.1rem'}}/>;
}

// Renders a supplier's Yes/No response to a negotiable commercial term (Payment Terms, Contract
// Length): accepted (green check), countered (red x + their alternative), or not yet answered.
function TermsAcceptanceValue({accepted, counterValue, i18n}) {
    if (accepted === null || accepted === undefined) {
        return <span className="text-muted">—</span>;
    }
    if (accepted) {
        return <span className="text-success"><i className="fas fa-check-circle mr-1"/>{i18n.labelTermsAccepted}</span>;
    }
    return (
        <span className="text-danger">
            <i className="fas fa-times-circle mr-1"/>
            {i18n.labelTermsCountered}{counterValue ? `: ${counterValue}` : ''}
        </span>
    );
}

// Renders a supplier's Yes/No feasibility answer to the Launch Timeframe (no counter-suggestion,
// unlike Payment Terms/Contract Length — just an always-available Notes line).
function LaunchTimeframeValue({canMeet, notes, i18n}) {
    if (canMeet === null || canMeet === undefined) {
        return <span className="text-muted">—</span>;
    }
    return (
        <div>
            {canMeet
                ? <span className="text-success"><i className="fas fa-check-circle mr-1"/>{i18n.labelYes}</span>
                : <span className="text-danger"><i className="fas fa-times-circle mr-1"/>{i18n.labelNo}</span>}
            {notes && <div className="font-size-xxs text-muted mt-1">{notes}</div>}
        </div>
    );
}

function InfoTooltip({text}) {
    if (!text) {
        return null;
    }
    return (
        <i className="far fa-question-circle text-muted ml-1" data-toggle="tooltip" data-placement="top" title={text}/>
    );
}

function primaryOrFirstProposalId(supplier) {
    if (!supplier.proposals || supplier.proposals.length === 0) {
        return null;
    }
    const primary = supplier.proposals.find((p) => p.isPrimary);
    return (primary || supplier.proposals[0]).id;
}

function initialProposalSelection(suppliers) {
    const initial = {};
    suppliers.forEach((s) => {
        initial[s.teamId] = primaryOrFirstProposalId(s);
    });
    return initial;
}

// Snapshots the left-to-right supplier order at the moment this is called (page load, or the
// "Sort by Incoterm" dropdown/arrows being used) — deliberately NOT recomputed whenever a proposal
// selector changes, so switching which proposal is shown for one supplier never reshuffles the
// other columns just because that proposal happens to be cheaper or pricier than before.
function sortTeamIdsByYearlyCost(suppliers, proposalSelection, incotermId, direction) {
    const withCost = suppliers.map((supplier) => {
        const proposal = supplier.proposals.find((p) => p.id === proposalSelection[supplier.teamId]);
        const aggregate = proposal?.aggregatesByIncoterm[incotermId];
        return {teamId: supplier.teamId, cost: aggregate ? aggregate.yearlyCost : null};
    });
    if (direction) {
        withCost.sort((a, b) => {
            if (a.cost === null && b.cost === null) return 0;
            if (a.cost === null) return 1;
            if (b.cost === null) return -1;
            const cmp = a.cost - b.cost;
            return direction === 'asc' ? cmp : -cmp;
        });
    }
    return withCost.map((s) => s.teamId);
}

function SummaryCards({comparison, incotermId, selectedProposalBySupplier, i18n}) {
    const responded = comparison.suppliers.filter((s) => s.status === 'responded');
    const candidateCosts = responded
        .map((s) => {
            const proposalId = selectedProposalBySupplier[s.teamId];
            const proposal = s.proposals.find((p) => p.id === proposalId);
            const aggregate = proposal && proposal.aggregatesByIncoterm[incotermId];
            return aggregate ? aggregate.yearlyCost : null;
        })
        .filter((cost) => cost !== null && cost !== undefined);

    const bestYearlyPrice = candidateCosts.length ? Math.min(...candidateCosts) : null;
    const maxSavingsPercentage = (bestYearlyPrice !== null && comparison.currentYearlyCost)
        ? Math.round(((comparison.currentYearlyCost - bestYearlyPrice) / comparison.currentYearlyCost) * 1000) / 10
        : null;
    const isSaving = maxSavingsPercentage !== null && maxSavingsPercentage >= 0;
    const savingsColorClass = maxSavingsPercentage === null ? '' : (isSaving ? 'text-success' : 'text-danger');

    return (
        <div className="d-flex flex-row flex-wrap mb-3">
            <div className="border rounded p-3 mr-3 mb-2" style={{minWidth: '200px', background: 'white'}}>
                <div className="text-uppercase font-size-xxs text-muted">{i18n.summaryCurrentYearlyCost}</div>
                <div className="font-weight-bold h5 mb-0">{formatMoney(comparison.currentYearlyCost, comparison.currencyCode)}</div>
            </div>
            <div className="border rounded p-3 mr-3 mb-2" style={{minWidth: '200px', background: 'white'}}>
                <div className="text-uppercase font-size-xxs text-muted">{i18n.summaryBestYearlyPrice}</div>
                <div className={`font-weight-bold h5 mb-0 ${savingsColorClass}`}>{formatMoney(bestYearlyPrice, comparison.currencyCode)}</div>
            </div>
            <div className="border rounded p-3 mr-3 mb-2" style={{minWidth: '200px', background: 'white'}}>
                <div className="text-uppercase font-size-xxs text-muted">{i18n.summaryMaxSavings}</div>
                <div className={`font-weight-bold h5 mb-0 ${savingsColorClass}`}>{formatPercentage(maxSavingsPercentage)}</div>
            </div>
            <div className="border rounded p-3 mb-2" style={{minWidth: '200px', background: 'white'}}>
                <div className="text-uppercase font-size-xxs text-muted">{i18n.summaryResponsesReceived}</div>
                <div className="font-weight-bold h5 mb-0">{responded.length}/{comparison.suppliers.length}</div>
            </div>
        </div>
    );
}

function SortArrows({active, direction, onSort}) {
    return (
        <span className="ml-1" style={{cursor: 'pointer'}}>
            <i
                className={`fas fa-caret-up ${active && direction === 'asc' ? 'text-dark' : 'text-muted'}`}
                onClick={() => onSort('asc')}
            />
            <i
                className={`fas fa-caret-down ml-1 ${active && direction === 'desc' ? 'text-dark' : 'text-muted'}`}
                onClick={() => onSort('desc')}
            />
        </span>
    );
}

function StatusBadge({status, responseDate, i18n}) {
    const labels = {responded: i18n.labelResponded, pending: i18n.labelPending, declined: i18n.labelDeclined};
    const classes = {responded: 'badge-success', pending: 'badge-secondary', declined: 'badge-danger'};
    return (
        <div>
            <span className={`badge ${classes[status]}`}>{labels[status]}</span>
            {status === 'responded' && responseDate && (
                <div className="font-size-xxs text-muted mt-1">
                    {new Date(responseDate).toLocaleDateString()}
                </div>
            )}
        </div>
    );
}

function ProposalSelector({supplier, selectedProposalId, onChange}) {
    if (!supplier.proposals || supplier.proposals.length === 0) {
        return null;
    }
    const selected = supplier.proposals.find((p) => p.id === selectedProposalId) || supplier.proposals[0];

    return (
        <div className="mt-1">
            {supplier.proposals.length > 1 ? (
                <select
                    className="form-control form-control-sm"
                    value={selectedProposalId || ''}
                    onChange={(e) => onChange(supplier.teamId, Number(e.target.value))}
                >
                    {supplier.proposals.map((p) => (
                        <option key={p.id} value={p.id}>{p.name || `Proposal ${p.id}`}</option>
                    ))}
                </select>
            ) : (
                <div className="font-weight-bold font-size-xs">{selected.name}</div>
            )}
            {selected.description && (
                <div className="font-size-xxs text-muted mt-1">{selected.description}</div>
            )}
        </div>
    );
}

function RebatePills({rebateTiers, i18n}) {
    if (!rebateTiers || rebateTiers.length === 0) {
        return <span>{i18n.labelNoRebates}</span>;
    }
    return (
        <div className="d-flex flex-column">
            {rebateTiers.map((tier, index) => (
                <span key={index} style={{whiteSpace: 'normal', textAlign: 'center'}}>{tier}</span>
            ))}
        </div>
    );
}

// Renders a qualitative-profile section (Company Information / Logistics & Shipping /
// Additional Information) as rows within the same table as whatever's above it, so
// each supplier's values line up directly under that supplier's columns above, rather than in a
// separate card layout. leadingColSpan defaults to 6 to match the Advanced pricing grid's SKU/
// quantity/price reference columns; the Simple table (no pricing grid, just one label column) passes
// leadingColSpan={1} and skips the (otherwise unused) buyerValue reference cell entirely.
function ProfileSectionRows({title, open, onToggle, fields, orderedSuppliers, incoterms, stickyLeft, leadingColSpan = 7}) {
    const supplierColumnCount = orderedSuppliers.length * incoterms.length;
    const hasReferenceColumn = leadingColSpan > 1;

    return (
        <>
            <tr style={{cursor: 'pointer'}} onClick={onToggle}>
                <td colSpan={leadingColSpan} className="bg-light font-weight-bold" style={stickyLeftStyle(stickyLeft)}>
                    {title}
                    <i className={`fas fa-chevron-${open ? 'up' : 'down'} ml-2`}/>
                </td>
                {supplierColumnCount > 0 && <td colSpan={supplierColumnCount} className="bg-light"/>}
            </tr>
            {open && fields.map((field) => (
                <tr key={field.key}>
                    <td style={{...stickyLeftStyle(stickyLeft), background: REFERENCE_BG}} className="font-weight-bold">
                        {field.label}
                        <InfoTooltip text={field.tooltip}/>
                    </td>
                    {hasReferenceColumn && (
                        <td colSpan={leadingColSpan - 1} style={{background: REFERENCE_BG}}>
                            {field.buyerValue}
                        </td>
                    )}
                    {orderedSuppliers.map((supplier) => (
                        <td
                            key={supplier.teamId}
                            colSpan={incoterms.length}
                            className="text-center"
                            style={supplier.status !== 'responded' ? {background: MUTED_BG} : {}}
                        >
                            {field.value(supplier) || '—'}
                        </td>
                    ))}
                </tr>
            ))}
        </>
    );
}

function AdvancedPricingComparisonTable({comparison, exportUrl, i18n}) {
    const incoterms = comparison.incoterms || [];
    const [incotermId, setIncotermId] = useState(incoterms[0]?.id);
    const [selectedProposalBySupplier, setSelectedProposalBySupplier] = useState(() => initialProposalSelection(comparison.suppliers));
    const [skuSort, setSkuSort] = useState(null);
    // Defaults to ascending so "Sort by Incoterm" is meaningfully sorted (lowest total yearly cost
    // first) from the moment the page loads, not just after the buyer notices the separate Yearly
    // Cost row arrows. This only tracks the arrow icons' active direction now — the actual column
    // order lives in `sortedTeamIds` below, a snapshot taken only when this dropdown or the arrows
    // are used, not on every render.
    const [yearlyCostSort, setYearlyCostSort] = useState('asc');
    // Frozen left-to-right order, re-snapshotted only by the incoterm dropdown's onChange and the
    // Yearly Cost row's sort arrows — never by a proposal selector change, so switching which
    // proposal is shown for one supplier can't reshuffle the other columns (see
    // sortTeamIdsByYearlyCost's comment).
    const [sortedTeamIds, setSortedTeamIds] = useState(() =>
        sortTeamIdsByYearlyCost(comparison.suppliers, initialProposalSelection(comparison.suppliers), incoterms[0]?.id, 'asc')
    );
    const [companyStatsOpen, setCompanyStatsOpen] = useState(true);
    const [logisticsOpen, setLogisticsOpen] = useState(true);
    const [commercialTermsOpen, setCommercialTermsOpen] = useState(true);
    const [documentsOpen, setDocumentsOpen] = useState(true);
    const [expandedNoteLines, setExpandedNoteLines] = useState(() => new Set());
    const [stickyLeft, setStickyLeft] = useState(0);
    const containerRef = useRef(null);
    const {rejectedTeamIds, filterRejected, pendingRejectSupplier, requestReject, cancelReject, confirmReject} = useSupplierRejection(comparison);

    useEffect(() => {
        const updateStickyLeft = () => {
            if (!containerRef.current) {
                return;
            }
            const scrollX = window.scrollX || document.documentElement.scrollLeft || 0;
            setStickyLeft(containerRef.current.getBoundingClientRect().left + scrollX);
        };
        updateStickyLeft();
        window.addEventListener('resize', updateStickyLeft);
        document.addEventListener('transitionend', updateStickyLeft);
        return () => {
            window.removeEventListener('resize', updateStickyLeft);
            document.removeEventListener('transitionend', updateStickyLeft);
        };
    }, []);

    const toggleNotesForLine = (lineId) => {
        setExpandedNoteLines((prev) => {
            const next = new Set(prev);
            if (next.has(lineId)) {
                next.delete(lineId);
            } else {
                next.add(lineId);
            }
            return next;
        });
    };

    const orderedLines = useMemo(() => {
        const lines = [...comparison.orderLines];
        if (skuSort) {
            lines.sort((a, b) => {
                const cmp = (a.lotName || '').localeCompare(b.lotName || '');
                return skuSort === 'asc' ? cmp : -cmp;
            });
        }
        return lines;
    }, [comparison.orderLines, skuSort]);

    // Order is purely a lookup against the frozen `sortedTeamIds` snapshot — deliberately does not
    // depend on `selectedProposalBySupplier`/`incotermId` directly, so re-rendering after a proposal
    // switch reuses whatever order was last snapshotted instead of silently re-sorting.
    const orderedSuppliers = useMemo(() => {
        const suppliers = filterRejected(comparison.suppliers);
        const byTeamId = new Map(suppliers.map((s) => [s.teamId, s]));
        const ordered = sortedTeamIds.map((teamId) => byTeamId.get(teamId)).filter(Boolean);
        const missing = suppliers.filter((s) => !sortedTeamIds.includes(s.teamId));
        return [...ordered, ...missing];
    }, [comparison.suppliers, sortedTeamIds, rejectedTeamIds]);

    const resortByYearlyCost = (direction) => {
        setYearlyCostSort(direction);
        setSortedTeamIds(sortTeamIdsByYearlyCost(comparison.suppliers, selectedProposalBySupplier, incotermId, direction));
    };

    const handleProposalChange = (teamId, proposalId) => {
        setSelectedProposalBySupplier((prev) => ({...prev, [teamId]: proposalId}));
    };

    const proposalFor = (supplier) => supplier.proposals.find((p) => p.id === selectedProposalBySupplier[supplier.teamId]);

    const companyInformationFields = [
        {key: 'turnover', label: i18n.labelTurnover, tooltip: i18n.tooltipTurnover, value: (s) => formatUnitOrRaw(s.profile.companyInformation.turnover, 'M')},
        {key: 'employees', label: i18n.labelEmployees, value: (s) => formatUnitOrRaw(s.profile.companyInformation.employees, 'employees')},
        {key: 'companyAge', label: i18n.labelCompanyAge, value: (s) => formatUnitOrRaw(s.profile.companyInformation.companyAge, 'years')},
        {key: 'location', label: i18n.labelLocation, value: (s) => s.profile.companyInformation.location},
        {key: 'clients', label: i18n.labelClients, tooltip: i18n.tooltipClients, value: (s) => formatMultilineList(s.profile.companyInformation.clients)},
        {key: 'countries', label: i18n.labelCountriesSupplied, tooltip: i18n.tooltipCountriesSupplied, value: (s) => formatMultilineList(s.profile.companyInformation.countries)},
        {key: 'productRange', label: i18n.labelProductRange, tooltip: i18n.tooltipProductRange, value: (s) => s.profile.companyInformation.productRange}
    ];

    const buyerLogistics = comparison.buyerLogistics || {};

    // Pallet type/height/weight and master carton weight are entered per-proposal (like
    // prices/notes), not in the static profile hash, alongside a buyerValue showing what the
    // buyer originally asked for in the same units, so the buyer can reference their own request.
    const logisticsFields = [
        {key: 'warehouseLocations', label: i18n.labelWarehouseLocations, tooltip: i18n.tooltipWarehouseLocations, value: (s) => formatMultilineList(s.profile.logistics.warehouseLocations)},
        {
            key: 'canMeetLogisticsRequirements',
            label: i18n.labelCanMeetLogisticsRequirements,
            value: (s) => <BooleanCheckIndicator canMeet={proposalFor(s)?.canMeetLogisticsRequirements}/>
        },
        {
            key: 'palletType',
            label: i18n.labelPalletType,
            tooltip: i18n.tooltipPalletType,
            value: (s) => proposalFor(s)?.palletType,
            buyerValue: buyerLogistics.palletType
        },
        {
            key: 'palletHeightCm',
            label: i18n.labelPalletHeight,
            tooltip: i18n.tooltipPalletHeight,
            value: (s) => formatUnit(proposalFor(s)?.palletHeightCm, 'cm'),
            buyerValue: formatUnit(buyerLogistics.palletHeightCm, 'cm')
        },
        {
            key: 'palletWeightKg',
            label: i18n.labelPalletWeight,
            tooltip: i18n.tooltipPalletWeight,
            value: (s) => formatUnit(proposalFor(s)?.palletWeightKg, 'kg'),
            buyerValue: formatUnit(buyerLogistics.maxPalletWeightKg, 'kg')
        },
        {
            key: 'masterCartonWeightKg',
            label: i18n.labelMasterCartonWeight,
            tooltip: i18n.tooltipMasterCartonWeight,
            value: (s) => formatUnit(proposalFor(s)?.masterCartonWeightKg, 'kg'),
            buyerValue: formatUnit(buyerLogistics.masterCartonWeightKg, 'kg')
        }
    ];

    const buyerTerms = comparison.buyerTerms || {};

    const commercialTermsFields = [
        {
            key: 'paymentTerms',
            label: i18n.labelPaymentTerms,
            value: (s) => {
                const proposal = proposalFor(s);
                return proposal && <TermsAcceptanceValue accepted={proposal.paymentTermsAccepted} counterValue={proposal.supplierPaymentTerms} i18n={i18n}/>;
            },
            buyerValue: buyerTerms.paymentTerms
        },
        {
            key: 'contractLength',
            label: i18n.labelContractLength,
            value: (s) => {
                const proposal = proposalFor(s);
                return proposal && <TermsAcceptanceValue accepted={proposal.contractLengthAccepted} counterValue={proposal.supplierContractLength} i18n={i18n}/>;
            },
            buyerValue: buyerTerms.contractLength
        },
        {
            key: 'launchTimeframe',
            label: i18n.labelLaunchTimeframe,
            value: (s) => {
                const proposal = proposalFor(s);
                return proposal && <LaunchTimeframeValue canMeet={proposal.canMeetLaunchTimeframe} notes={proposal.launchTimeframeNotes} i18n={i18n}/>;
            },
            buyerValue: formatDateRange(buyerTerms.launchMinDeadline, buyerTerms.launchMaxDeadline)
        }
    ];

    const documentsFields = [
        {
            key: 'additionalNotes',
            label: i18n.labelSupplierNotes,
            value: (s) => s.profile.documents.additionalNotes
        },
        {
            key: 'documents',
            label: i18n.labelAdditionalInfo,
            value: (s) => {
                const items = s.profile.documents.items || [];
                if (items.length === 0) {
                    return null;
                }
                return (
                    <div className="d-flex flex-column align-items-center">
                        {items.map((item) => (
                            <a key={item.id} href={item.url} target="_blank" rel="noreferrer" className="font-size-xs">
                                <i className="far fa-file-lines mr-1"/>
                                {item.filename}
                            </a>
                        ))}
                    </div>
                );
            }
        }
    ];

    return (
        <div className="pricing-comparison-table" ref={containerRef}>
            {pendingRejectSupplier && (
                <ConfirmDialog
                    title={i18n.labelRejectSupplierTitle}
                    body={i18n.labelRejectSupplierBody}
                    cancelLabel={i18n.labelCancel}
                    confirmLabel={i18n.labelRejectConfirm}
                    onCancel={cancelReject}
                    onConfirm={confirmReject}
                />
            )}
            <div className="d-flex flex-row justify-content-between align-items-center mb-3">
                <SummaryCards comparison={comparison} incotermId={incotermId} selectedProposalBySupplier={selectedProposalBySupplier} i18n={i18n}/>
            </div>

            <div className="d-flex flex-row justify-content-between align-items-center mb-2">
                <div className="d-flex flex-row align-items-center">
                    <label className="mb-0 mr-2">{i18n.labelSortByIncoterm}</label>
                    <select
                        className="form-control form-control-sm"
                        style={{width: 'auto'}}
                        value={incotermId}
                        onChange={(e) => {
                            const newIncotermId = Number(e.target.value);
                            setIncotermId(newIncotermId);
                            // Selecting an incoterm re-sorts suppliers left-to-right by that
                            // incoterm's total yearly cost, lowest to highest, rather than leaving
                            // them in whatever order (or manual desc sort) was previously in effect.
                            // Uses newIncotermId directly rather than resortByYearlyCost's closure
                            // over the not-yet-updated incotermId state.
                            setYearlyCostSort('asc');
                            setSortedTeamIds(sortTeamIdsByYearlyCost(comparison.suppliers, selectedProposalBySupplier, newIncotermId, 'asc'));
                        }}
                    >
                        {incoterms.map((incoterm) => (
                            <option key={incoterm.id} value={incoterm.id}>{incoterm.code}</option>
                        ))}
                    </select>
                </div>
                {exportUrl && (
                    <a className="btn secondary-button" href={exportUrl}>
                        <i className="fas fa-file-excel mr-1"/>
                        {i18n.labelDownloadExcel}
                    </a>
                )}
            </div>

            <div>
                <table className="table" style={{minWidth: '100%'}}>
                    <thead>
                        <tr>
                            <th colSpan={7}/>
                            {orderedSuppliers.map((supplier) => (
                                <th key={supplier.teamId} colSpan={incoterms.length} className="text-center" style={supplier.status !== 'responded' ? {background: MUTED_BG} : {}}>
                                    <div className="d-flex justify-content-end">
                                        <SupplierActionsMenu i18n={i18n} onReject={() => requestReject(supplier)} onMessage={() => messageSupplier(comparison, supplier.teamId)}/>
                                    </div>
                                    <div className="font-weight-bold">{supplier.companyName}</div>
                                    <ProposalSelector supplier={supplier} selectedProposalId={selectedProposalBySupplier[supplier.teamId]} onChange={handleProposalChange}/>
                                    <div className="mt-1"><StatusBadge status={supplier.status} responseDate={supplier.responseDate} i18n={i18n}/></div>
                                </th>
                            ))}
                        </tr>
                        <tr>
                            <th style={{background: REFERENCE_BG, position: 'sticky', left: stickyLeft, zIndex: 2, minWidth: '220px'}}>
                                {i18n.labelSkuColumn}
                                <SortArrows active={!!skuSort} direction={skuSort} onSort={(dir) => setSkuSort(skuSort === dir ? null : dir)}/>
                            </th>
                            <th style={{background: REFERENCE_BG, minWidth: '140px'}}>{i18n.labelRecurringOrder}</th>
                            <th style={{background: REFERENCE_BG, minWidth: '110px'}}>{i18n.labelYearlyVolume}</th>
                            <th style={{background: REFERENCE_BG, minWidth: '110px'}}>{i18n.labelUnitOfMeasure}</th>
                            <th style={{background: REFERENCE_BG, minWidth: '110px'}}>{i18n.labelCurrentCost}</th>
                            <th style={{background: REFERENCE_BG, minWidth: '110px'}}>{i18n.labelTargetCost}</th>
                            <th style={{background: REFERENCE_BG, minWidth: '110px'}}>{i18n.labelTargetRetail}</th>
                            {orderedSuppliers.map((supplier) => (
                                incoterms.map((incoterm) => (
                                    <th key={`${supplier.teamId}-${incoterm.id}`} className="text-center" style={{minWidth: '130px'}}>
                                        {incoterm.code}
                                    </th>
                                ))
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {orderedLines.map((line) => {
                            const hasAnyNotes = orderedSuppliers.some((supplier) => proposalFor(supplier)?.notes?.[line.id]) || line.note;
                            const notesOpen = expandedNoteLines.has(line.id);

                            return (
                                <React.Fragment key={line.id}>
                                    <tr>
                                        <td style={{...stickyLeftStyle(stickyLeft), background: REFERENCE_BG}}>
                                            <div className="font-weight-bold">{line.lotName}</div>
                                            {hasAnyNotes ? (
                                                <span
                                                    className="badge badge-light text-muted"
                                                    style={{cursor: 'pointer'}}
                                                    onClick={() => toggleNotesForLine(line.id)}
                                                >
                                                    <i className="far fa-sticky-note mr-1"/>
                                                    {i18n.labelViewNotes}
                                                    <i className={`fas fa-chevron-${notesOpen ? 'up' : 'down'} ml-1`}/>
                                                </span>
                                            ) : (
                                                <span className="font-size-xxs text-muted">{i18n.labelNoNotes}</span>
                                            )}
                                        </td>
                                        <td style={{background: REFERENCE_BG}}>
                                            {formatNumber(line.recurringQuantity)} / {line.frequency}
                                        </td>
                                        <td style={{background: REFERENCE_BG}}>{formatNumber(line.yearlyVolume)}</td>
                                        <td style={{background: REFERENCE_BG}}>{line.unitOfMeasure}</td>
                                        <td style={{background: REFERENCE_BG}}>{formatMoney(line.currentPrice, comparison.currencyCode)}</td>
                                        <td style={{background: REFERENCE_BG}}>{formatMoney(line.targetCostPrice, comparison.currencyCode)}</td>
                                        <td style={{background: REFERENCE_BG}}>{formatMoney(line.targetRetailPrice, comparison.currencyCode)}</td>
                                        {orderedSuppliers.map((supplier) => {
                                            const proposal = proposalFor(supplier);
                                            return incoterms.map((incoterm) => (
                                                <td key={`${supplier.teamId}-${incoterm.id}-${line.id}`} className="text-center" style={supplier.status !== 'responded' ? {background: MUTED_BG} : {}}>
                                                    {proposal ? formatMoney(proposal.prices[line.id]?.[incoterm.id], comparison.currencyCode) : '—'}
                                                </td>
                                            ));
                                        })}
                                    </tr>
                                    {notesOpen && (
                                        <tr>
                                            <td style={{...stickyLeftStyle(stickyLeft), background: REFERENCE_BG}} className="font-weight-bold">
                                                {i18n.labelViewNotes}
                                            </td>
                                            <td colSpan={6} style={{background: REFERENCE_BG}}>
                                                {line.note && <span className="font-size-xxs text-muted">{line.note}</span>}
                                            </td>
                                            {orderedSuppliers.map((supplier) => {
                                                const note = proposalFor(supplier)?.notes?.[line.id];
                                                return (
                                                    <td
                                                        key={supplier.teamId}
                                                        colSpan={incoterms.length}
                                                        className="text-center font-size-xs"
                                                        style={supplier.status !== 'responded' ? {background: MUTED_BG} : {}}
                                                    >
                                                        {note || '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}

                        <tr className="border-top">
                            <td style={{...stickyLeftStyle(stickyLeft), background: REFERENCE_BG}} className="font-weight-bold">
                                {i18n.labelYearlyCost}
                                <InfoTooltip text={i18n.tooltipYearlyCost}/>
                                <SortArrows active={!!yearlyCostSort} direction={yearlyCostSort} onSort={(dir) => resortByYearlyCost(yearlyCostSort === dir ? null : dir)}/>
                            </td>
                            <td colSpan={6} style={{background: REFERENCE_BG}}>
                            </td>
                            {orderedSuppliers.map((supplier) => {
                                const proposal = proposalFor(supplier);
                                const aggregate = proposal?.aggregatesByIncoterm[incotermId];
                                return (
                                    <td key={supplier.teamId} colSpan={incoterms.length} className="text-center" style={supplier.status !== 'responded' ? {background: MUTED_BG} : {}}>
                                        {aggregate ? (
                                            <>
                                                <div className="font-weight-bold">{formatMoney(aggregate.yearlyCost, comparison.currencyCode)}</div>
                                                {aggregate.savings && (
                                                    <div className={`font-size-xxs ${aggregate.savings.amount >= 0 ? 'text-success' : 'text-danger'}`}>
                                                        {formatMoney(aggregate.savings.amount, comparison.currencyCode)} ({formatPercentage(aggregate.savings.percentage)})
                                                    </div>
                                                )}
                                            </>
                                        ) : '—'}
                                    </td>
                                );
                            })}
                        </tr>
                        <tr>
                            <td style={{...stickyLeftStyle(stickyLeft), background: REFERENCE_BG}} className="font-weight-bold">
                                {i18n.labelYearlyMargin}
                                <InfoTooltip text={i18n.tooltipYearlyMargin}/>
                            </td>
                            <td colSpan={6} style={{background: REFERENCE_BG}}>
                            </td>
                            {orderedSuppliers.map((supplier) => {
                                const proposal = proposalFor(supplier);
                                const aggregate = proposal?.aggregatesByIncoterm[incotermId];
                                return (
                                    <td key={supplier.teamId} colSpan={incoterms.length} className="text-center" style={supplier.status !== 'responded' ? {background: MUTED_BG} : {}}>
                                        {aggregate ? formatMoney(aggregate.yearlyMargin, comparison.currencyCode) : '—'}
                                    </td>
                                );
                            })}
                        </tr>
                        <tr>
                            <td style={{...stickyLeftStyle(stickyLeft), background: REFERENCE_BG}} className="font-weight-bold">
                                {i18n.labelUnitMargin}
                                <InfoTooltip text={i18n.tooltipUnitMargin}/>
                            </td>
                            <td colSpan={6} style={{background: REFERENCE_BG}}>
                            </td>
                            {orderedSuppliers.map((supplier) => {
                                const proposal = proposalFor(supplier);
                                const aggregate = proposal?.aggregatesByIncoterm[incotermId];
                                return (
                                    <td key={supplier.teamId} colSpan={incoterms.length} className="text-center" style={supplier.status !== 'responded' ? {background: MUTED_BG} : {}}>
                                        {aggregate ? formatMoney(aggregate.unitMargin, comparison.currencyCode) : '—'}
                                    </td>
                                );
                            })}
                        </tr>
                        <tr>
                            <td style={{...stickyLeftStyle(stickyLeft), background: REFERENCE_BG}} className="font-weight-bold">
                                {i18n.labelCanMeetPackagingRequirements}
                            </td>
                            <td colSpan={6} style={{background: REFERENCE_BG}}>
                            </td>
                            {orderedSuppliers.map((supplier) => {
                                const proposal = proposalFor(supplier);
                                return (
                                    <td key={supplier.teamId} colSpan={incoterms.length} className="text-center" style={supplier.status !== 'responded' ? {background: MUTED_BG} : {}}>
                                        <BooleanCheckIndicator canMeet={proposal?.canMeetPackagingRequirements}/>
                                        {proposal?.packagingDetailsNotes && <div className="font-size-xxs text-muted mt-1">{proposal.packagingDetailsNotes}</div>}
                                    </td>
                                );
                            })}
                        </tr>
                        <tr>
                            <td style={{...stickyLeftStyle(stickyLeft), background: REFERENCE_BG}} className="font-weight-bold">
                                {i18n.labelRebateConditions}
                            </td>
                            <td colSpan={6} style={{background: REFERENCE_BG}}>
                            </td>
                            {orderedSuppliers.map((supplier) => {
                                const proposal = proposalFor(supplier);
                                return (
                                    <td key={supplier.teamId} colSpan={incoterms.length} className="text-center" style={supplier.status !== 'responded' ? {background: MUTED_BG} : {}}>
                                        {proposal ? <RebatePills rebateTiers={proposal.rebateTiers} i18n={i18n}/> : '—'}
                                    </td>
                                );
                            })}
                        </tr>
                        <tr>
                            <td style={{...stickyLeftStyle(stickyLeft), background: REFERENCE_BG}} className="font-weight-bold">
                                {i18n.labelFulfillmentDetails}
                            </td>
                            <td colSpan={6} style={{background: REFERENCE_BG}}>
                            </td>
                            {orderedSuppliers.map((supplier) => {
                                const proposal = proposalFor(supplier);
                                return (
                                    <td key={supplier.teamId} colSpan={incoterms.length} className="text-center" style={supplier.status !== 'responded' ? {background: MUTED_BG} : {}}>
                                        {proposal ? <FulfillmentDetailsList lines={orderedLines} proposal={proposal} i18n={i18n}/> : '—'}
                                    </td>
                                );
                            })}
                        </tr>

                        <ProfileSectionRows
                            title={i18n.labelCompanyInformation}
                            open={companyStatsOpen}
                            onToggle={() => setCompanyStatsOpen(!companyStatsOpen)}
                            fields={companyInformationFields}
                            orderedSuppliers={orderedSuppliers}
                            incoterms={incoterms}
                            stickyLeft={stickyLeft}
                        />
                        <ProfileSectionRows
                            title={i18n.labelLogisticsShipping}
                            open={logisticsOpen}
                            onToggle={() => setLogisticsOpen(!logisticsOpen)}
                            fields={logisticsFields}
                            orderedSuppliers={orderedSuppliers}
                            incoterms={incoterms}
                            stickyLeft={stickyLeft}
                        />
                        <ProfileSectionRows
                            title={i18n.labelCommercialTerms}
                            open={commercialTermsOpen}
                            onToggle={() => setCommercialTermsOpen(!commercialTermsOpen)}
                            fields={commercialTermsFields}
                            orderedSuppliers={orderedSuppliers}
                            incoterms={incoterms}
                            stickyLeft={stickyLeft}
                        />
                        <ProfileSectionRows
                            title={i18n.labelAdditionalInfo}
                            open={documentsOpen}
                            onToggle={() => setDocumentsOpen(!documentsOpen)}
                            fields={documentsFields}
                            orderedSuppliers={orderedSuppliers}
                            incoterms={incoterms}
                            stickyLeft={stickyLeft}
                        />
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// specs/007-simple-advanced-pricing-request, research.md Decision 5 (revised) — Simple requests
// have no per-SKU pricing or negotiated terms to compare (Product SKUs and proposals are
// Advanced-only by construction), but the qualitative supplier profile (Company Information,
// Logistics & Shipping, Additional Information) exists regardless of type, so this reuses the same
// ProfileSectionRows building block as the Advanced table for a consistent look — just without the
// pricing grid, incoterm breakdown, or the fields that only ever apply to a priced proposal
// (Logistics dimensions, Commercial Terms).
function SimplePricingRequestsTable({comparison, exportUrl, i18n}) {
    const [companyStatsOpen, setCompanyStatsOpen] = useState(true);
    const [supplierExperienceOpen, setSupplierExperienceOpen] = useState(true);
    const [documentsOpen, setDocumentsOpen] = useState(true);
    const [stickyLeft, setStickyLeft] = useState(0);
    const containerRef = useRef(null);
    const {filterRejected, pendingRejectSupplier, requestReject, cancelReject, confirmReject} = useSupplierRejection(comparison);

    useEffect(() => {
        const updateStickyLeft = () => {
            if (!containerRef.current) {
                return;
            }
            const scrollX = window.scrollX || document.documentElement.scrollLeft || 0;
            setStickyLeft(containerRef.current.getBoundingClientRect().left + scrollX);
        };
        updateStickyLeft();
        window.addEventListener('resize', updateStickyLeft);
        document.addEventListener('transitionend', updateStickyLeft);
        return () => {
            window.removeEventListener('resize', updateStickyLeft);
            document.removeEventListener('transitionend', updateStickyLeft);
        };
    }, []);

    const orderedSuppliers = filterRejected(comparison.suppliers);
    // ProfileSectionRows derives its per-supplier colSpan from incoterms.length — Simple has no
    // incoterms, so this single-item placeholder gives exactly one column per supplier.
    const singleColumn = [{id: 'single'}];

    const companyInformationFields = [
        {key: 'turnover', label: i18n.labelTurnover, tooltip: i18n.tooltipTurnover, value: (s) => formatUnitOrRaw(s.profile.companyInformation.turnover, 'M')},
        {key: 'employees', label: i18n.labelEmployees, value: (s) => formatUnitOrRaw(s.profile.companyInformation.employees, 'employees')},
        {key: 'companyAge', label: i18n.labelCompanyAge, value: (s) => formatUnitOrRaw(s.profile.companyInformation.companyAge, 'years')},
        {key: 'location', label: i18n.labelLocation, value: (s) => s.profile.companyInformation.location},
        {key: 'clients', label: i18n.labelClients, tooltip: i18n.tooltipClients, value: (s) => formatMultilineList(s.profile.companyInformation.clients)},
        {key: 'countries', label: i18n.labelCountriesSupplied, tooltip: i18n.tooltipCountriesSupplied, value: (s) => formatMultilineList(s.profile.companyInformation.countries)},
        {key: 'productRange', label: i18n.labelProductRange, tooltip: i18n.tooltipProductRange, value: (s) => s.profile.companyInformation.productRange}
    ];

    const supplierExperienceFields = [
        {key: 'warehouseLocations', label: i18n.labelWarehouseLocations, tooltip: i18n.tooltipWarehouseLocations, value: (s) => formatMultilineList(s.profile.logistics.warehouseLocations)}
    ];

    const documentsFields = [
        {key: 'additionalNotes', label: i18n.labelSupplierNotes, value: (s) => s.profile.documents.additionalNotes},
        {
            key: 'documents',
            label: i18n.labelAdditionalInfo,
            value: (s) => {
                const items = s.profile.documents.items || [];
                if (items.length === 0) {
                    return null;
                }
                return (
                    <div className="d-flex flex-column align-items-center">
                        {items.map((item) => (
                            <a key={item.id} href={item.url} target="_blank" rel="noreferrer" className="font-size-xs">
                                <i className="far fa-file-lines mr-1"/>
                                {item.filename}
                            </a>
                        ))}
                    </div>
                );
            }
        }
    ];

    return (
        <div className="pricing-comparison-table" ref={containerRef}>
            {pendingRejectSupplier && (
                <ConfirmDialog
                    title={i18n.labelRejectSupplierTitle}
                    body={i18n.labelRejectSupplierBody}
                    cancelLabel={i18n.labelCancel}
                    confirmLabel={i18n.labelRejectConfirm}
                    onCancel={cancelReject}
                    onConfirm={confirmReject}
                />
            )}
            {exportUrl && (
                <div className="d-flex flex-row justify-content-end mb-2">
                    <a className="btn secondary-button" href={exportUrl}>
                        <i className="fas fa-file-excel mr-1"/>
                        {i18n.labelDownloadExcel}
                    </a>
                </div>
            )}
            <table className="table" style={{minWidth: '100%'}}>
                <thead>
                    <tr>
                        <th style={{background: REFERENCE_BG, position: 'sticky', left: stickyLeft, zIndex: 2, minWidth: '220px'}}/>
                        {orderedSuppliers.map((supplier) => (
                            <th key={supplier.teamId} className="text-center" style={supplier.status !== 'responded' ? {background: MUTED_BG} : {}}>
                                <div className="d-flex justify-content-end">
                                    <SupplierActionsMenu i18n={i18n} onReject={() => requestReject(supplier)} onMessage={() => messageSupplier(comparison, supplier.teamId)}/>
                                </div>
                                <div className="font-weight-bold">{supplier.companyName}</div>
                                <div className="mt-1"><StatusBadge status={supplier.status} responseDate={supplier.responseDate} i18n={i18n}/></div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <ProfileSectionRows
                        title={i18n.labelCompanyInformation}
                        open={companyStatsOpen}
                        onToggle={() => setCompanyStatsOpen(!companyStatsOpen)}
                        fields={companyInformationFields}
                        orderedSuppliers={orderedSuppliers}
                        incoterms={singleColumn}
                        stickyLeft={stickyLeft}
                        leadingColSpan={1}
                    />
                    <ProfileSectionRows
                        title={i18n.labelSupplierExperience}
                        open={supplierExperienceOpen}
                        onToggle={() => setSupplierExperienceOpen(!supplierExperienceOpen)}
                        fields={supplierExperienceFields}
                        orderedSuppliers={orderedSuppliers}
                        incoterms={singleColumn}
                        stickyLeft={stickyLeft}
                        leadingColSpan={1}
                    />
                    <ProfileSectionRows
                        title={i18n.labelAdditionalInfo}
                        open={documentsOpen}
                        onToggle={() => setDocumentsOpen(!documentsOpen)}
                        fields={documentsFields}
                        orderedSuppliers={orderedSuppliers}
                        incoterms={singleColumn}
                        stickyLeft={stickyLeft}
                        leadingColSpan={1}
                    />
                </tbody>
            </table>
        </div>
    );
}

export default function PricingComparisonTable({comparison, exportUrl, i18n}) {
    if (comparison.requestType === 'simple') {
        return <SimplePricingRequestsTable comparison={comparison} exportUrl={exportUrl} i18n={i18n}/>;
    }

    return <AdvancedPricingComparisonTable comparison={comparison} exportUrl={exportUrl} i18n={i18n}/>;
}
